import { useCallback, useEffect, useRef, useState } from "react";
import type { MirrorPresenceState } from "@/components/mirror/MirrorPresenceConfig";

// Presence state is independent from avatar assets. No external animation
// is selected or preloaded by this hook.
export type MirrorAnimationState =
  | "idle"
  | "idleBreak"
  | "listening"
  | "thinking"
  | "speaking";

interface UseMirrorStateOptions {
  isVoiceListening: boolean;
  isTTSSpeaking: boolean;
  // Optional state input; only presence is resolved here, never clip URLs.
  mirrorState?: MirrorAnimationState;
}

interface UseMirrorStateReturn {
  presenceState: MirrorPresenceState;
  animationUrl?: undefined;
  startThinking: () => void;
  goIdle: () => void;
  // Compatibility fields stay undefined; consumers use their embedded clip.
  stateAnimations?: undefined;
  effectiveAnimationState?: MirrorAnimationState;
}

const IDLE_BREAK_DELAY_MS = 30_000;
const IDLE_BREAK_DURATION_MS = 2_500;

export function useMirrorState(options: UseMirrorStateOptions): UseMirrorStateReturn {
  const { isVoiceListening, isTTSSpeaking, mirrorState } = options;

  const [presenceState, setPresenceState] = useState<MirrorPresenceState>("idle");
  const [isIdleBreak, setIsIdleBreak] = useState(false);

  const idleBreakTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleBreakDurationRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stateRef = useRef<MirrorPresenceState>(presenceState);
  useEffect(() => {
    stateRef.current = presenceState;
  }, [presenceState]);

  // Effect — sync voice listening
  useEffect(() => {
    if (isVoiceListening) {
      if (stateRef.current === "idle") {
        setPresenceState("listening");
      }
    } else {
      if (stateRef.current === "listening") {
        setPresenceState("idle");
      }
    }
  }, [isVoiceListening]);

  // Effect — sync TTS speaking
  useEffect(() => {
    if (isTTSSpeaking) {
      if (stateRef.current !== "speaking") {
        setPresenceState("speaking");
      }
    } else {
      if (stateRef.current === "speaking") {
        setPresenceState("idle");
      }
    }
  }, [isTTSSpeaking]);

  // Effect — schedule idleBreak after sustained idle
  useEffect(() => {
    if (idleBreakTimerRef.current) {
      clearTimeout(idleBreakTimerRef.current);
      idleBreakTimerRef.current = null;
    }

    if (presenceState === "idle") {
      idleBreakTimerRef.current = setTimeout(() => {
        setIsIdleBreak(true);
        idleBreakTimerRef.current = null;
      }, IDLE_BREAK_DELAY_MS);
    } else {
      setIsIdleBreak(false);
    }

    return () => {
      if (idleBreakTimerRef.current) {
        clearTimeout(idleBreakTimerRef.current);
        idleBreakTimerRef.current = null;
      }
    };
  }, [presenceState]);

  // Effect — auto-close idleBreak after one cycle
  useEffect(() => {
    if (idleBreakDurationRef.current) {
      clearTimeout(idleBreakDurationRef.current);
      idleBreakDurationRef.current = null;
    }
    if (isIdleBreak) {
      idleBreakDurationRef.current = setTimeout(() => {
        setIsIdleBreak(false);
        idleBreakDurationRef.current = null;
      }, IDLE_BREAK_DURATION_MS);
    }

    return () => {
      if (idleBreakDurationRef.current) {
        clearTimeout(idleBreakDurationRef.current);
        idleBreakDurationRef.current = null;
      }
    };
  }, [isIdleBreak]);

  const startThinking = useCallback(() => {
    setPresenceState("thinking");
  }, []);

  const goIdle = useCallback(() => {
    setPresenceState("idle");
  }, []);

  const effectiveAnimationState =
    mirrorState !== undefined
      ? mirrorState === "idle" && isIdleBreak
        ? "idleBreak"
        : mirrorState
      : undefined;

  return {
    presenceState,
    animationUrl: undefined,
    startThinking,
    goIdle,
    stateAnimations: undefined,
    effectiveAnimationState,
  };
}
